import { useState } from "react";
import { Plus, Truck, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { StatusBadge, WarehouseBadge } from "@/components/Badges";
import { SalesOrderDialog, DispatchDialog } from "@/components/OrderDialogs";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { fmtINR, dateLabel } from "@/lib/format";

export default function SalesOrders() {
  const [status, setStatus] = usePref("sales_orders-status", "all");
  const { data } = useLoad(() => api.get("/sales-orders", { params: status === "all" ? {} : { status } }).then((r) => r.data), [status]);
  const [edit, setEdit] = useState({ open: false, order: null });
  const [dispatch, setDispatch] = useState({ open: false, order: null });
  const cancel = async (o) => {
    const msg = o.status === "dispatched" ? "Cancel this dispatched order? Its stock OUT entries will be reversed." : "Cancel this order?";
    if (!window.confirm(msg)) return;
    try { await api.post(`/sales-orders/${o.id}/cancel`); toast.success("Order cancelled"); notifyStockChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Customer Sales Orders" subtitle="Book orders; stock is deducted (OUT) from the chosen warehouse when dispatched.">
        <SimpleSelect value={status} onChange={setStatus} testid="so-status-filter"
          options={[["all", "All statuses"], ["pending", "Pending"], ["dispatched", "Dispatched"], ["cancelled", "Cancelled"]]} />
        <Button size="sm" onClick={() => setEdit({ open: true, order: null })} data-testid="new-sales-order-btn"><Plus className="h-4 w-4" /> New sales order</Button>
      </PageHeader>
      {!data ? <Loading /> : (
        <div className="panel overflow-auto">
          <table className="data-table" data-testid="sales-orders-table">
            <thead><tr><th>Order</th><th>Date</th><th>Customer</th><th>Items</th><th>WH</th><th className="text-right">Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.map((o) => (
                <tr key={o.id} data-testid={`so-row-${o.order_no}`}>
                  <td className="font-mono font-bold text-primary">{o.order_no}</td>
                  <td className="text-xs">{dateLabel(o.order_date)}{o.dispatched_date && <div className="text-muted-foreground">sent {dateLabel(o.dispatched_date)}</div>}</td>
                  <td><div className="font-medium">{o.customer}</div><div className="text-xs text-muted-foreground">{o.phone}</div></td>
                  <td className="text-xs max-w-[320px]">{o.items.map((i) => <div key={i.product_id} className="truncate"><span className="font-mono">{i.qty}×</span> {i.name}</div>)}</td>
                  <td><WarehouseBadge value={o.warehouse} /></td>
                  <td className="num">{fmtINR(o.total)}</td>
                  <td><StatusBadge value={o.status} /></td>
                  <td className="whitespace-nowrap">
                    {o.status === "pending" && <>
                      <Button size="sm" variant="outline" onClick={() => setDispatch({ open: true, order: o })} data-testid={`dispatch-btn-${o.order_no}`}><Truck className="h-4 w-4" /> Dispatch</Button>
                      <button className="p-1.5 text-muted-foreground hover:text-primary" onClick={() => setEdit({ open: true, order: o })} data-testid={`edit-so-${o.order_no}`}><Pencil className="h-4 w-4" /></button>
                    </>}
                    {o.status !== "cancelled" && <button className="p-1.5 text-muted-foreground hover:text-red-400" onClick={() => cancel(o)} title="Cancel" data-testid={`cancel-so-${o.order_no}`}><Ban className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
              {!data.length && <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">No sales orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <SalesOrderDialog open={edit.open} order={edit.order} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} />
      <DispatchDialog open={dispatch.open} order={dispatch.order} onOpenChange={(o) => setDispatch((s) => ({ ...s, open: o }))} />
    </div>
  );
}
